import queryString from 'query-string';
import { useReducer } from 'react';
import { useApplePayAvailable } from '../hooks/useApplePayAvailable';
import {EventAPINS} from "../api/event.api";

export namespace UIStoreNS {
  import EventRaw = EventAPINS.EventRaw;
  export type ThemeType = {
    primary: string | null;
    secondary: string | null;
    background: string | null;
    surface: string | null;
    error: string | null;
    onPrimary: string | null;
    onSecondary: string | null;
    onBackground: string | null;
    onSurface: string | null;
    onError: string | null;
  } | null;

  export type State = {
    isIsolatedMode: boolean;
    modalConfig: ModalConfig;
    metaTagsConfig: MetaTagsConfig;
    theme: ThemeType;
    displayCountry: 'is' | 'es' | 'nb' | 'dk';
  };

  export type Store = {
    reducer: [State, React.Dispatch<Action>];
    applePayAvailableData: { applePayAvailable: boolean; applePayAvailableLoading: boolean };
    closeModal: () => void;
  };

  export type BookingSelectionPayload = {
    title: string;
    type: 'BookingSelection';
    restaurantId: number;
    restaurantSlug: string;
    date: Date;
    callback: (action: 'event' | 'table', payload?: EventRaw) => void;
    onClose: () => void;
    isTableAvailable: boolean;
  };

  type WaitingListTimesPayload = {
    type: 'WaitingListTimes';
    restaurantId: number;
    restaurantSlug: string;
    guests: number;
    date: string;
    title: string;
    isWaitingListWhenFullyBooked: boolean;
    onClose: () => void;
  };

  type AvailableTimesPayload = {
    type: 'AvailableTimes';
    restaurantId: number;
    restaurantSlug: string;
    title: string;
    onClose: () => void;
  };

  type ModalConfig =
    | { type: 'None'; onClose: undefined }
    | BookingSelectionPayload
    | AvailableTimesPayload
    | WaitingListTimesPayload;

  export type MetaTagsConfig = {
    pageTitle: string;
    shareTitle: string;
    description: string;
    imageUrl: string;
    siteUrl: string;
  };

  export enum ActionType {
    Isolated,
    ModalConfig,
    UpdateMetaTags,
    Theme,
    DisplayCountry,
    ApplePayAvailable,
  }

  export type Action =
    | { type: ActionType.Isolated | ActionType.ApplePayAvailable; payload: boolean }
    | { type: ActionType.ModalConfig; payload: ModalConfig }
    | { type: ActionType.UpdateMetaTags; payload: MetaTagsConfig }
    | { type: ActionType.Theme; payload: ThemeType }
    | { type: ActionType.DisplayCountry; payload: State['displayCountry'] };
}

export const useUIStore = () => {

  // @ts-ignore
  const { isolation: qsIsolation }: { isolation: string } = queryString.parse(
    window.location.search,
  );

  const initState: UIStoreNS.State = {
    isIsolatedMode: !!qsIsolation && qsIsolation.includes('true'),
    modalConfig: { type: 'None', onClose: undefined },
    metaTagsConfig: {
      pageTitle: 'Dineout.is - Bókaðu borð á bestu veitingastöðum landsins',
      shareTitle: 'Dineout.is - Bókaðu borð á bestu veitingastöðum landsins',
      description: 'Bókaðu borð á bestu veitingastöðum landsins með einum smelli.',
      imageUrl:
        'https://res.cloudinary.com/dineout/image/upload/v1595278474/production/static/facebook-share/Frame_12_lcervg.png',
      siteUrl: window.location.href,
    },
    theme: null,
    displayCountry: 'is',
  };

  const storeReducer = (storeState: UIStoreNS.State, action: UIStoreNS.Action): UIStoreNS.State => {
    switch (action.type) {
      case UIStoreNS.ActionType.Isolated:
        return { ...storeState, isIsolatedMode: action.payload };
      case UIStoreNS.ActionType.ModalConfig:
        return { ...storeState, modalConfig: action.payload };
      case UIStoreNS.ActionType.UpdateMetaTags:
        return { ...storeState, metaTagsConfig: action.payload };
      case UIStoreNS.ActionType.Theme:
        return { ...storeState, theme: action.payload };
      case UIStoreNS.ActionType.DisplayCountry:
        return { ...storeState, displayCountry: action.payload };
      default:
        throw new Error('Could not find state type in UI store');
    }
  };

  const reducer = useReducer(storeReducer, initState);
  const [, dispatch] = reducer;

  const applePayAvailableData = useApplePayAvailable();

  const closeModal = () =>
    dispatch({
      type: UIStoreNS.ActionType.ModalConfig,
      payload: { type: 'None', onClose: undefined },
    });

  return { reducer, applePayAvailableData, closeModal };
};
